<template>
  <div>
    <Button
      v-for="button in actionButtons"
      :id="button.id"
      :key="button.id"
      :value="button.value"
      :href="button.href"
      :variant="button.variant"
      size="medium"
      :class="button.class"
      @click="handleButtonClick(button)"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import Button from '@/components/button/Button.vue';

interface Job {
  id: string;
  status: string;
  isActive: boolean;
  parent: {
    id: string;
  };
  applications: {
    edges: Array<{
      node: {
        status: string;
      };
    }>;
  };
}

type ButtonVariant = 'primary' | 'outlined';

interface ButtonConfig {
  id: string;
  value: string;
  href?: string;
  variant: ButtonVariant;
  class: string;
  onClick?: () => void;
  show: boolean;
}

export default defineComponent({
  name: 'JobActions',
  components: {
    Button,
  },
  props: {
    job: {
      type: Object as () => Job,
      required: true,
    },
    accountType: {
      type: String,
      required: true,
    },
    accountID: {
      type: String,
      required: true,
    },
  },
  emits: ['close-job'],
  computed: {
    jobState() {
      return {
        isActive: this.job.isActive,
        hasApplicants: this.totalOpenApplicants.length > 0,
        isClosed: this.job.status === 'JOB_STATUS_CLOSED',
        currentUserOwnsJob: this.accountID === this.job.parent.id,
        isFCAccount: this.accountType === 'family_concierge',
        isActiveOrPending: this.job.isActive || this.job.status === 'PENDING',
      };
    },
    totalOpenApplicants(): Array<{ node: { status: string } }> {
      return this.job.applications.edges.filter((app) => app.node.status === 'open');
    },
    actionButtons(): ButtonConfig[] {
      const { isActive, hasApplicants, isClosed, currentUserOwnsJob, isFCAccount, isActiveOrPending } = this.jobState;

      const buttons: ButtonConfig[] = [
        {
          id: 'applicants-job',
          value: 'View Applicants',
          href: `/jobs/${this.job.id}/applications`,
          variant: isActive ? 'primary' : 'outlined',
          class: 'w-full mb-16',
          show: hasApplicants && (isActive || isClosed),
        },
        {
          id: 'share-job',
          value: 'Share Job',
          href: this.shareJob(),
          variant: 'outlined',
          class: 'w-full mt-16',
          show: isFCAccount && isActive && currentUserOwnsJob,
        },
        {
          id: 'edit-job',
          value: 'Edit job',
          href: this.jobAction('edit'),
          variant: 'outlined',
          class: 'w-full mb-16 mt-16',
          show: isActiveOrPending && currentUserOwnsJob,
        },
        {
          id: 'close-job',
          value: 'Close job',
          variant: 'outlined',
          class: 'w-full',
          onClick: () => this.$emit('close-job'),
          show: isActiveOrPending && currentUserOwnsJob,
        },
        {
          id: 'reopen-job',
          value: 'Re-open job',
          href: this.jobAction('reopen'),
          variant: 'primary',
          class: 'w-full mb-16',
          show: (!isActiveOrPending || isClosed) && currentUserOwnsJob,
        },
      ];

      return buttons.filter((button) => button.show);
    },
  },
  methods: {
    jobAction(jobAction: string): string {
      return `/jobs/action_with_uuid?job_action=${jobAction}&uuid=${this.job.id}`;
    },
    shareJob(): string {
      return `/share-jobs?job_id=${this.job.id}`;
    },
    handleButtonClick(button: ButtonConfig): void {
      // You will gt a test warning about an invalid click handler without this
      if (button.onClick) {
        button.onClick();
      }
    },
  },
});
</script>
